import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from "react";
import { useAtom } from "jotai";
import MGAAPI from "../../api/index";
import { useASelectedCountry } from "../../hooks/useASelectedCountry";
import displayError from "../../constants/utils";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import { useTranslation } from 'react-i18next';
import { useForm } from "@mantine/form";
import set from "lodash/set";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import PVTabs from "./PVTabs";
import { defaultComponentsAtom } from "../../utils/atoms/defaultComponentAtom";
import {
  mgaRegex,
  useAssessmentRouteParams,
  useCurrentDesignComponentLists,
} from "../../constants/helper";
import { PVIncentivesAtom } from "../../utils/atoms/PVIncentivesAtom";
import cloneDeep from "lodash/cloneDeep";
import {
  validateProfileStartDate,
  validateProfileStartTime,
  validateProfileTimeResolution,
  validateProfileUnits,
  validatePowNomRef,
  validateFile,
  validateRelDCPower,
  validateInvEfficiency,
  validateRelACPower,
  validateTilt,
  validateOrientation,
  validateSystemLoss,
  validateCreateProfileButtonClicked,
  validatePvCapexCost,
  validateInvLifetime,
  validatePvOmCost,
  validatePvLifetime,
  validateInvOmCost,
  validateInvCapexCost,
  computeNominalAcPower,
  computeRelACPower,
} from "./PVValidation";

const MGA_API = new MGAAPI();
const LoadPV = forwardRef((props, ref) => {
  const { pvComponent, projectDetails, setLoadingComp, loadingComp } = props;
  const { t } = useTranslation();
  const [, setComponentStatus] = useAtom(componentStatusAtom);

  const [componentDetails, setComponentDetails] = useAtom(componentsAtom);
  const [, setProjectDetails] = useAtom(projectDetailsAtom);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );
  const [defaultComponents, setDefaultComponents] = useAtom(
    defaultComponentsAtom
  );
  const [incentivesData, setIncentivesData] = useAtom(PVIncentivesAtom);

  const form = useForm({
    key: JSON.stringify(componentDetails?.pv), // Forces re-render when pv data updates
    initialValues: {
      ...componentDetails?.pv,
      componentName: componentDetails?.pv?.componentName || "",
      componentAttributes: {
        ...componentDetails?.pv?.componentAttributes,
        pvParameters: {
          ...componentDetails?.pv?.componentAttributes?.pvParameters,
          profileDetail: {
            ...componentDetails?.pv?.componentAttributes?.pvParameters
              ?.profileDetail,
            startDate: componentDetails?.pv?.componentAttributes?.pvParameters
              ?.profileDetail?.startDate
              ? new Date(
                  componentDetails?.pv?.componentAttributes?.pvParameters?.profileDetail?.startDate
                )
              : new Date(
                  new Date(
                    new Date().setDate(new Date().getDate() - 1)
                  ).setFullYear(new Date().getFullYear() - 1)
                ),
            startTime:
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.profileDetail?.startTime || "00:00",
            timeResolution:
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.profileDetail?.timeResolution || "15",
            units:
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.profileDetail?.units || "kW",
          }
        },
      },
    },
    validateInputOnBlur: [
      "componentAttributes.pvParameters.relDCPower",
      "componentAttributes.pvParameters.relACPower",
      "componentAttributes.pvParameters.invEfficiency",
      "componentAttributes.pvParameters.systemLoss",
      "componentAttributes.pvParameters.tilt",
      "componentAttributes.pvParameters.orientation",
    ],
    validate: {
      componentAttributes: {
        pvParameters: {
          profileDetail: {
            startDate: (value) =>
              validateProfileStartDate(
                value,
                componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
                t,
              ),
            startTime: (value) =>
              validateProfileStartTime(
                value,
                componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
                t,
              ),
            timeResolution: (value) =>
              validateProfileTimeResolution(
                value,
                componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
                t,
              ),
            units: (value) =>
              validateProfileUnits(
                value,
                componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
                t,
              ),
          },
          powNomRef: (value) =>
            validatePowNomRef(
              value,
              t,
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.relACPower,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
            ),
          file: (value) =>
            validateFile(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.isUploadPvFileButtonClicked,
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.profileFile?.length,
              t,
            ),
          degradationFactor: (value) =>
            String(value).trim() === ""
              ? t("pv.degradationRequired")
              : value && !mgaRegex?.zeroToNinetyNineWithDecimal?.test(value)
                ? t("pv.degradationRange")
                : null,
          relDCPower: (value) =>
            validateRelDCPower(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              t,
            ),
          invEfficiency: (value) =>
            validateInvEfficiency(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              t,
            ),
          relACPower: (value) =>
            validateRelACPower(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              t,
            ),
          tilt: (value) =>
            validateTilt(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              t,
            ),
          orientation: (value) =>
            validateOrientation(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              t,
            ),
          systemLoss: (value) =>
            validateSystemLoss(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              t,
            ),
          createProfileButtonClicked: () =>
            validateCreateProfileButtonClicked(
              componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.createProfileData?.length,
              componentDetails?.pv?.componentAttributes?.pvParameters
                ?.createProfileButtonClicked,
              t,
            ),
        },
      },
      economicAttributes: {
        pvParameters: {
          pvCapexCost: (value) =>
            validatePvCapexCost(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup,
              t,
            ),
          invLifetime: (value) => validateInvLifetime(value, t),
          pvOmCost: (value) => validatePvOmCost(value, t),
          pvLifetime: (value) => validatePvLifetime(value, t),
          invOmCost: (value) => validateInvOmCost(value, t),
          invCapexCost: (value) =>
            validateInvCapexCost(
              value,
              componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup,
              t,
            ),
        },
      },
    },
  });
  const selectedCountry = useASelectedCountry();
  const { setValues } = form;

  // Backfill defaults into global state so saves/effects read consistent values
  useEffect(() => {
    const pvParams =
      componentDetails?.pv?.componentAttributes?.pvParameters || {};
    const isUploadPV =
      componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV;
    if (!isUploadPV) {
      const needsTilt =
        pvParams.tilt === undefined || pvParams.tilt === null || pvParams.tilt === "";
      const profileEmpty = !pvParams.createProfileData || pvParams.createProfileData.length === 0;
      const latitudeValue = projectDetails?.locationDTO?.latitude;
      // Only set defaults if missing and no generated profile exists to avoid unintended resets
      if (needsTilt && profileEmpty) {
        let defaultTilt;
        if (latitudeValue !== undefined && latitudeValue !== null) {
          const absLat = Math.abs(latitudeValue);
          if (absLat < 25) {
            defaultTilt = (absLat * 0.87).toFixed(2);
          } else if (absLat <= 50|| absLat > 50) {
            defaultTilt = ((absLat * 0.76) + 3.1).toFixed(2);
          } else {
            defaultTilt = form?.values?.componentAttributes?.pvParameters?.tilt ?? 15;
          }
        } else {
          defaultTilt = form?.values?.componentAttributes?.pvParameters?.tilt ?? 15;
        }
        updatePVData("componentAttributes", "tilt", defaultTilt);
      }
      if (profileEmpty) {
        const defaultOrientation =
          latitudeValue < 0 ? 0 : 180;
        updatePVData("componentAttributes", "orientation", defaultOrientation);
      }
    }
  }, [
    componentDetails?.pv?.componentName,
    componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
    projectDetails?.locationDTO?.latitude,
  ]);

  // Fetch incentives and store them in local state
  useEffect(() => {
    if (form?.values?.incentives?.length) {
      setIncentivesData(form?.values?.incentives);
    } else {
      const country = selectedCountry || "GEN";
      MGA_API.getPVIncentives("PV", country).then((res) => {
        setIncentivesData(res); // Keep API response in local state
      });
    }
  }, [form?.values?.incentives, selectedCountry]);

  const handlePvSubmit = form.onSubmit(
    (values) => {
      handlePVSave();
    },
    (errors) => {
      const firstErrorPath = Object.keys(errors)[0];
      const inputNode = form.getInputNode(firstErrorPath);
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  );

  useImperativeHandle(ref, () => ({
    handlePvSubmit,
  }));

  // Store initial values in refs to compare changes (must be outside useEffect)
  const initialInvEfficiencyRef = useRef();
  const initialRelDCPowerRef = useRef();

  useEffect(() => {
    const pvParams =
      componentDetails?.pv?.componentAttributes?.pvParameters || {};
    const { invEfficiency, relDCPower } =
      pvParams;
    // const isUploadPV =
    //   componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV;

    // if (isUploadPV) {
    //   const units = pvParams?.profileDetail?.units;
    //   let updatedUploadNominalAcPower = computeNominalAcPower(profileFile);
    //   if (units && units?.toLowerCase() === "w") {
    //     updatedUploadNominalAcPower = updatedUploadNominalAcPower / 1000;
    //   }
    //   updatePVData("componentAttributes", "powNomRef", updatedUploadNominalAcPower);
    //   return;
    // }

    if (typeof initialInvEfficiencyRef.current === "undefined") {
      initialInvEfficiencyRef.current = invEfficiency;
    }
    if (typeof initialRelDCPowerRef.current === "undefined") {
      initialRelDCPowerRef.current = relDCPower;
    }

    const unchanged =
      invEfficiency === initialInvEfficiencyRef.current &&
      relDCPower === initialRelDCPowerRef.current;
    if (unchanged) return;

    const relACPower = computeRelACPower(invEfficiency, relDCPower);
    updatePVData("componentAttributes", "relACPower", relACPower);
  }, [
    componentDetails?.pv?.componentAttributes?.pvParameters?.invEfficiency,
    componentDetails?.pv?.componentAttributes?.pvParameters?.relDCPower,
    componentDetails?.pv?.componentAttributes?.pvParameters?.createProfileData,
    componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
    // componentDetails?.pv?.componentAttributes?.pvParameters?.powNomRef,
    componentDetails?.pv?.componentAttributes?.pvParameters?.profileFile,
  ]);

  useEffect(() => {
    if (!componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV) {
      updatePVData(
        "componentAttributes",
        "createProfileData",
        componentDetails?.pv?.componentAttributes?.pvParameters?.profileFile
      );
      updatePVData("componentAttributes", "createProfileButtonClicked", true);
      updatePVData("componentAttributes", "profileFile", []);
      updatePVData("componentAttributes", "isUploadPvFileButtonClicked", false);
    } else if (componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV) {
      updatePVData(
        "componentAttributes",
        "profileFile",
        componentDetails?.pv?.componentAttributes?.pvParameters?.profileFile
      );
      updatePVData("componentAttributes", "isUploadPvFileButtonClicked", true);
      updatePVData("componentAttributes", "createProfileData", []);
      updatePVData("componentAttributes", "createProfileButtonClicked", false);
    }
  }, []);

  // Handler function to update the global state
  const updatePVData = (category, key, value, isNew = false) => {
    setComponentDetails((prev) => {
      const newState = { ...prev, currentComponent: "pv" };

      const { startDate, startTime, timeResolution, units } =
        newState?.pv?.componentAttributes?.pvParameters?.profileDetail || {};

      const createProfilekeys = [
        "relDCPower",
        "invEfficiency",
        "tilt",
        "orientation",
        "systemLoss",
      ];
      if (createProfilekeys?.includes(key)) {
        set(
          newState,
          "pv.componentAttributes.pvParameters.createProfileData",
          []
        );
        set(
          newState,
          "pv.componentAttributes.pvParameters.createProfileButtonClicked",
          false
        );
      }
      const uploadProfileKeys = [
        "profileDetail.startDate",
        "profileDetail.startTime",
        "profileDetail.units",
        "profileDetail.timeResolution",
      ];
      if (uploadProfileKeys?.includes(key)) {
        set(newState, "pv.componentAttributes.pvParameters.profileFile", []);
        set(
          newState,
          "pv.componentAttributes.pvParameters.isUploadPvFileButtonClicked",
          false
        );
        set(newState, "pv.componentAttributes.pvParameters.file", null);
      }
        

      if (!startDate || !startTime || !timeResolution || !units ) {
        set(
          newState,
          "pv.componentAttributes.pvParameters.profileDetail.startTime",
          "00:00"
        );
        set(
          newState,
          "pv.componentAttributes.pvParameters.profileDetail.timeResolution",
          15
        );
        set(
          newState,
          "pv.componentAttributes.pvParameters.profileDetail.units",
          "kW"
        );
        set(
          newState,
          "pv.componentAttributes.pvParameters.profileDetail.startDate",
          new Date(
            new Date(new Date().setDate(new Date().getDate() - 1)).setFullYear(
              new Date().getFullYear() - 1
            )
          )
        );
      }

      if (isNew) {
        // Use lodash set for mutation
        // set(newState, 'pv', defaultComponents?.pv || {});
        set(
          newState,
          "pv.uiAttributes",
          cloneDeep(defaultComponents?.pv?.uiAttributes || {})
        );
        set(
          newState,
          "pv.economicAttributes.pvParameters",
          cloneDeep(defaultComponents?.pv?.economicAttributes?.pvParameters || {})
        );
        set(
          newState,
          "pv.componentAttributes.pvParameters",
          cloneDeep(defaultComponents?.pv?.componentAttributes?.pvParameters || {})
        );

        set(newState, "pv.componentId", -1);
        set(newState, "pv.designVersionId", null);
        set(newState, "pv.designId", null);

        return newState;
      } else {
        // Use lodashSet to update nested property correctly
        if (category === "incentives") {
          set(newState, `pv.${category}`, key);
          setIncentivesData(key);
        } else if (category !== undefined) {
          set(newState, `pv.${category}.pvParameters.${key}`, value);
        }
        if (
          componentDetails?.pv?.componentAttributes?.pvParameters?.profileDetail
            ?.startDate
        ) {
          set(
            newState,
            "pv.componentAttributes.pvParameters.profileDetail.startDate",
            new Date(
              componentDetails?.pv?.componentAttributes?.pvParameters?.profileDetail?.startDate
            )
          );
        } else {
          set(
            newState,
            "pv.componentAttributes.pvParameters.profileDetail.startDate",
            new Date(
              new Date(
                new Date().setDate(new Date().getDate() - 1)
              ).setFullYear(new Date().getFullYear() - 1)
            )
          );
        }
        setValues(newState?.pv);
        return newState;
      }
    });
  };
  useEffect(() => {
    if (!form.isTouched()) return;
    setComponentStatus((prev) => ({
      ...prev,
      pv: { isEditing: true, completed: false },
    }));
  }, [form.values]);

  // Incentive validation guard (mirrors PVIncentive logic)
  const hasIncentiveErrors = () => {
    const incentives =
      form?.values?.incentives || componentDetails?.pv?.incentives || [];
    return incentives?.some((item) => {
      if (!item?.status) return false; // only validate selected incentives
      return item?.incentiveAttributes?.some((inc) => {
        const valueStr = (inc?.attributeDefaultValue ?? "").toString().trim();
        if (valueStr === "") return true; // required empty
        if (inc?.attributeRegex) {
          try {
            const regex = new RegExp(inc.attributeRegex);
            return !regex.test(valueStr);
          } catch (e) {
            // bad regex provided -> ignore pattern error
            return false;
          }
        }
        return false;
      });
    });
  };

  // Save Data
  const handlePVSave = async () => {
    // Block save if incentive validation fails
    if (hasIncentiveErrors()) {
      displayError(
        "error",
        t("pv.incentive.fixErrors")
      );
      return; // do not proceed to API call
    }
    setLoadingComp(true);
    const filteredComponent =
      selectedDesignAndDesignVersion?.componentDTOList.find(
        (comp) => comp.componentType === "PV"
      );

    const payload = {
      projectId: projectId || "",
      designId: designId || "",
      designVersionId: designVersionId || "",
      componentType: pvComponent?.componentType || "PV",
      componentId: componentDetails?.pv?.componentId ? componentDetails?.pv?.componentId :form.values?.componentId ? form.values.componentId : -1,
      componentName:
        componentDetails?.pv?.componentName || form.values?.componentType,
      componentAttributes: {
        pvParameters: {
          powNomRef: form?.values?.componentAttributes?.pvParameters?.powNomRef,
          degradationFactor: Number(form?.values?.componentAttributes?.pvParameters?.degradationFactor),
          profileDetail: componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? {
                startDate:
                  componentDetails?.pv?.componentAttributes?.pvParameters
                    ?.profileDetail?.startDate,
                timeResolution:
                  componentDetails?.pv?.componentAttributes?.pvParameters
                    ?.profileDetail?.timeResolution,
                units:
                  componentDetails?.pv?.componentAttributes?.pvParameters
                    ?.profileDetail?.units,
                startTime:
                  componentDetails?.pv?.componentAttributes?.pvParameters
                    ?.profileDetail?.startTime,
              }
            : null,
          profileFile: componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.profileFile?.length > 0
              ? componentDetails?.pv?.componentAttributes?.pvParameters
                  ?.profileFile
              : null
            : componentDetails?.pv?.componentAttributes?.pvParameters
                ?.createProfileData?.length > 0
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.createProfileData
            : null,
          relDCPower: !componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.relDCPower
            : null,
          invEfficiency: !componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.invEfficiency
            : null,
          relACPower: !componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.relACPower
            : null,
          tilt: !componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters?.tilt
            : null,
          orientation: !componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.orientation
            : null,
          systemLoss: !componentDetails?.pv?.uiAttributes?.pvParameters
            ?.isUploadPV
            ? componentDetails?.pv?.componentAttributes?.pvParameters
                ?.systemLoss
            : null,
        },
      },
      economicAttributes: componentDetails?.pv?.economicAttributes || {},
      uiAttributes: {
        pvParameters: {
          pvSetup:
            componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup || "new",
          isUploadPV:
            componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV,
        },
      },
      incentives:
        componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup === "new"
          ? componentDetails.pv?.incentives
          : [],
      datasource: componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV
        ? "Upload"
        : "OTHER",
    };
    try {
      updatePVData("componentAttributes", "file", null);
      await MGA_API.importLoad(payload).then((res) => {
        const newComponentId = res?.componentId ?? res?.componentID ?? res?.data?.componentId;
        if (newComponentId !== undefined && newComponentId !== null) {
          // Persist in global state and form values for consistency
          setComponentDetails((prev) => {
            const next = { ...prev };
            set(next, "pv.componentId", newComponentId);
            return next;
          });
          // setValues({ ...form.values, componentId: String(newComponentId) });
        }
      });
      setComponentStatus((prev) => ({
        ...prev,
        pv: {
          completed: true,
          isEditing: false,
        },
      }));
      form.resetTouched();
      // commented for performance issue
      MGA_API.editProject(projectId).then((res) => {
        setProjectDetails((prev) => ({
          ...prev,
          userId: res.userId || "",
          currencyCode: res?.projectDTO?.currencyCode || "$",
          projectDTO: res.projectDTO || {},
          componentDTOList:
            flattenCountComponentDTOList(res.countComponentDTOList) || [],
          designWithVersionsDTOList: res.designWithVersionsDTOList || [],
          locationDTO: res.locationDTO || {},
          countComponentDTOList: res.countComponentDTOList,
          designComponentsList:
            flattenDesignCountComponent(res?.designComponentsList) || [],
        }));
      });
      setLoadingComp(false);
      displayError("success", t('pv.savedSuccessfully'));
    } catch (error) {
      const errorMessage =
        error.response?.data || t('pv.saveError');
      displayError("error", errorMessage);
      setLoadingComp(false);
    }
  };
  return (
    <PVTabs
      updatePVData={updatePVData}
      componentDetails={componentDetails}
      form={form}
      setValues={setValues}
      handlePvSubmit={handlePvSubmit}
      setLoadingComp={setLoadingComp}
      loadingComp={loadingComp}
      incentivesData={incentivesData}
    />
  );
});
export default LoadPV;
